import type { ClickIds, MysiteAttribution, UtmParams } from './types';

const VISITOR_KEY = 'mysite_visitor_id';
const SESSION_KEY = 'mysite_session';
const ATTR_PREFIX = 'mysite_attr_';
const SESSION_TTL_MS = 30 * 60 * 1000;
const C_RE = /^\.pi(?<pi>\d+)\.pk(?<pk>\d+)\.ps(?<ps>[A-Za-z0-9_-]+)$/;

type SessionState = { id: string; started_at_utc: string; last_seen_ms: number };
export type IdentityState = { visitor_id: string; session_id: string; session_started_at_utc: string };

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const ATTR_KEYS = ['r', 'c', 'fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'msclkid', 'epik', 'rdt_cid', ...UTM_KEYS] as const;

export function identityState(now: Date): IdentityState {
  const visitorId = readOrCreateId(VISITOR_KEY, 'v');
  const session = readOrCreateSession(now);
  return { visitor_id: visitorId, session_id: session.id, session_started_at_utc: session.started_at_utc };
}

export function captureUrlAttribution(): void {
  const url = safeUrl(window.location.href);
  if (!url) return;
  for (const key of ATTR_KEYS) {
    const value = url.searchParams.get(key);
    if (value) writeStorage(`${ATTR_PREFIX}${key}`, value);
  }
}

export function mysiteAttribution(): MysiteAttribution {
  const r = readAttr('r');
  const c = readAttr('c');
  if (!c) return { r, c, pi: null, pk: null, ps: null, parse_status: 'missing' };
  const match = c.match(C_RE);
  if (!match?.groups) return { r, c, pi: null, pk: null, ps: null, parse_status: 'invalid' };
  return {
    r,
    c,
    pi: Number(match.groups.pi),
    pk: Number(match.groups.pk),
    ps: match.groups.ps,
    parse_status: 'parsed',
  };
}

export function utmParams(): UtmParams {
  return {
    utm_source: readAttr('utm_source'),
    utm_medium: readAttr('utm_medium'),
    utm_campaign: readAttr('utm_campaign'),
    utm_term: readAttr('utm_term'),
    utm_content: readAttr('utm_content'),
  };
}

export function clickIds(): ClickIds {
  ensureMetaCookies();
  ensureTtpCookie();
  return {
    fbclid: readAttr('fbclid'),
    fbc: getCookie('_fbc'),
    fbp: getCookie('_fbp'),
    gclid: readAttr('gclid'),
    gbraid: readAttr('gbraid'),
    wbraid: readAttr('wbraid'),
    ttclid: readAttr('ttclid'),
    ttp: getCookie('_ttp'),
    msclkid: readAttr('msclkid'),
    epik: readAttr('epik'),
    rdt_cid: readAttr('rdt_cid'),
  };
}

function readOrCreateId(key: string, prefix: string): string {
  const existing = readStorage(key);
  if (existing) return existing;
  const id = `${prefix}_${uuid()}`;
  writeStorage(key, id);
  writeCookie(key, id, 365);
  return id;
}

function readOrCreateSession(now: Date): SessionState {
  const raw = readStorage(SESSION_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (isSessionState(parsed) && now.getTime() - parsed.last_seen_ms < SESSION_TTL_MS) {
        const updated = { ...parsed, last_seen_ms: now.getTime() };
        writeStorage(SESSION_KEY, JSON.stringify(updated));
        return updated;
      }
    } catch {
      // Create a new session below.
    }
  }
  const session = { id: `s_${uuid()}`, started_at_utc: now.toISOString(), last_seen_ms: now.getTime() };
  writeStorage(SESSION_KEY, JSON.stringify(session));
  return session;
}

function isSessionState(value: unknown): value is SessionState {
  if (!value || typeof value !== 'object') return false;
  return (
    'id' in value &&
    'started_at_utc' in value &&
    'last_seen_ms' in value &&
    typeof value.id === 'string' &&
    typeof value.started_at_utc === 'string' &&
    typeof value.last_seen_ms === 'number'
  );
}

function ensureMetaCookies(): void {
  const fbclid = readAttr('fbclid');
  if (fbclid && !getCookie('_fbc')) writeCookie('_fbc', `fb.1.${Date.now()}.${fbclid}`, 90);
  if (!getCookie('_fbp')) writeCookie('_fbp', `fb.1.${Date.now()}.${randomDigits()}`, 90);
}

function ensureTtpCookie(): void {
  if (!getCookie('_ttp')) writeCookie('_ttp', `tt.1.${Date.now()}.${randomDigits()}`, 90);
}

function readAttr(key: string): string | null {
  const url = safeUrl(window.location.href);
  return url?.searchParams.get(key) ?? readStorage(`${ATTR_PREFIX}${key}`);
}

function getCookie(name: string): string | null {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1] ?? '') : null;
}

function writeCookie(name: string, value: string, days: number): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${days * 86400}; Path=/; SameSite=Lax; Secure`;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Storage unavailable.
    }
  }
}

function uuid(): string {
  return typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${randomDigits()}-${randomDigits()}`;
}

function randomDigits(): string {
  const array = new Uint32Array(2);
  crypto.getRandomValues(array);
  return `${array[0]}${array[1]}`;
}

function safeUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}
